import { CustomerStatus } from './types';
import { STATUS_CONFIG } from './constants';

// Valid onboarding status values from backend
const VALID_STATUSES: CustomerStatus[] = ['draft', 'pending_initial', 'invited', 'not_approved', 'due_diligence', 'approved'];

/**
 * Map the real onboarding_status string from backend to CustomerStatus.
 *
 * `is_approved=true` wins over an unfinished onboarding_status. Some
 * prod data (including the DE Avanti-sourced profiles) has
 * onboarding_status='draft' alongside is_approved=true as a migration
 * artefact — those customers should still be treated as approved.
 */
export const mapStatusToType = (
  onboardingStatus: string | undefined,
  isApproved?: boolean,
): CustomerStatus => {
  if (isApproved) return 'approved';
  if (onboardingStatus && VALID_STATUSES.includes(onboardingStatus as CustomerStatus)) {
    return onboardingStatus as CustomerStatus;
  }
  return 'draft';
};

export const getStatusButtonClass = (status: CustomerStatus, isSelected: boolean): string => {
  const config = STATUS_CONFIG[status];
  if (isSelected) {
    return `${config.bgColor} border-2 border-primary`;
  }
  return config.bgColor;
};

/**
 * CR-4497: Get styling for customer risk level badges.
 * Returns color classes and label for the given risk level.
 *
 * @param riskLevel - The risk level string
 * @returns Style config with badgeColor, label, and icon identifier
 */
export const getRiskLevelStyle = (riskLevel: string): { badgeColor: string; label: string; icon: string } => {
  switch (riskLevel) {
    case 'critical':
      return { badgeColor: 'bg-red-600 text-white', label: 'Critical Risk', icon: 'critical' };
    case 'high':
      return { badgeColor: 'bg-red-100 text-red-800 border-red-200', label: 'High Risk', icon: 'high' };
    case 'medium':
      return { badgeColor: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Medium Risk', icon: 'medium' };
    case 'low':
      return { badgeColor: 'bg-green-100 text-green-800 border-green-200', label: 'Low Risk', icon: 'low' };
    default:
      return { badgeColor: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Unknown Risk', icon: 'medium' };
  }
};
